/**
 * DrillThroughRecord — per `rpm-drill-through-record-page.html`.
 * Two-pane nCino record grammar: breached-sessions list on the left, detail
 * record on the right with overview / timeline / attempts / notes sub-tabs.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button, Pill } from '@/components/primitives';
import { FIXTURE_BREACHED_SESSIONS } from './fixtures';

type SubTab = 'overview' | 'timeline' | 'attempts' | 'notes';

export function DrillThroughRecord(): JSX.Element {
  const { kpiKey } = useParams<{ kpiKey: string }>();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string>(FIXTURE_BREACHED_SESSIONS[0]?.applicantId ?? '');
  const [sub, setSub] = useState<SubTab>('overview');

  const selected = FIXTURE_BREACHED_SESSIONS.find((s) => s.applicantId === selectedId);

  const kpiTitle =
    kpiKey === 'sla-breaches' ? 'SLA Breaches Today' :
    kpiKey === 'tasks-overdue' ? 'Tasks Overdue' :
    kpiKey === 'approvals-pending' ? 'Approvals Pending' :
    'Sessions Active';

  return (
    <div className="module-page">
      <button
        type="button"
        className="btn btn-link"
        onClick={() => navigate('..')}
        style={{ marginBottom: 'var(--sp-6)' }}
      >
        ← Back to RPM
      </button>

      <div className="rpm-drill-banner">
        <span aria-hidden style={{ fontSize: 24 }}>⚠</span>
        <div>
          <h1>{kpiTitle}</h1>
          <div style={{ fontSize: 12, color: 'var(--err-text)', marginTop: 4 }}>
            {FIXTURE_BREACHED_SESSIONS.length} breached · 7 at risk · Oldest 42 min over SLA
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--sp-5)' }}>
          <Button variant="ghost" size="sm">Configure thresholds</Button>
          <Button variant="danger" size="sm">Escalate to Manager</Button>
        </div>
      </div>

      <div className="rpm-drill">
        <div className="rpm-drill__list">
          <div className="rpm-drill__list-head">
            <h3 style={{ fontSize: 13, color: 'var(--pcb-blue-dark)' }}>Breached sessions <Pill variant="err">{FIXTURE_BREACHED_SESSIONS.length}</Pill></h3>
          </div>

          {FIXTURE_BREACHED_SESSIONS.map((s) => {
            const isActive = selectedId === s.applicantId;
            const over = s.elapsedMin - s.slaMin;
            return (
              <button
                key={s.applicantId}
                type="button"
                className={`rpm-drill__item ${isActive ? 'is-active' : ''}`.trim()}
                onClick={() => setSelectedId(s.applicantId)}
                aria-current={isActive ? 'true' : undefined}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
                  <span className="rpm-drill__item-name">{s.applicantName}</span>
                  <Pill variant={s.slaState === 'breached' ? 'err' : s.slaState === 'atRisk' ? 'warn' : 'ok'}>
                    {s.slaState === 'breached' ? 'Breached' : s.slaState === 'atRisk' ? 'At risk' : 'Within'}
                  </Pill>
                </div>
                <div className="rpm-drill__item-meta">
                  {s.applicantId} · {s.officer} · elapsed <strong style={{ color: 'var(--pcb-red)' }}>+{over}m</strong>
                </div>
                <div className="rpm-drill__item-meta">{s.stage} · {s.product} · {s.branch}</div>
              </button>
            );
          })}
        </div>

        <div className="rpm-drill__detail">
          {selected ? (
            <>
              <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-6)', marginBottom: 'var(--sp-5)' }}>
                <h2 style={{ fontSize: 20, color: 'var(--pcb-blue-dark)' }}>{selected.applicantName}</h2>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-600)' }}>{selected.applicantId}</span>
              </header>
              <div
                role="status"
                style={{
                  background: 'var(--err-bg)',
                  color: 'var(--err-text)',
                  padding: 'var(--sp-5) var(--sp-7)',
                  borderLeft: '4px solid var(--pcb-red)',
                  borderRadius: 'var(--r-sm)',
                  fontSize: 13,
                  marginBottom: 'var(--sp-7)',
                }}
              >
                ● SLA BREACHED · {selected.elapsedMin - selected.slaMin} min over SLA window
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--sp-5)', marginBottom: 'var(--sp-7)' }}>
                <MetaCell label="Officer" value={`${selected.officerInitials} — ${selected.officer}`} />
                <MetaCell label="Stage" value={selected.stage} />
                <MetaCell label="Elapsed" value={`${selected.elapsedMin}m`} tone="red" />
                <MetaCell label="SLA window" value={`${selected.slaMin}m`} />
              </div>

              <div style={{ display: 'flex', gap: 'var(--sp-4)', marginBottom: 'var(--sp-7)' }}>
                <Button variant="ghost" size="sm">Message officer</Button>
                <Button variant="ghost" size="sm">Reassign</Button>
                <Button variant="primary" size="sm">Open session</Button>
              </div>

              <div className="rpm-drill__subtabs" role="tablist" aria-label="Record detail tabs">
                {(['overview', 'timeline', 'attempts', 'notes'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={sub === t}
                    className={`rpm-drill__subtab ${sub === t ? 'is-active' : ''}`.trim()}
                    onClick={() => setSub(t)}
                  >
                    {t[0]?.toUpperCase()}{t.slice(1)}
                  </button>
                ))}
              </div>

              {sub === 'overview' && (
                <div>
                  <h3 style={{ fontSize: 13, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 'var(--sp-5)' }}>
                    Session at a glance
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4) var(--sp-8)' }}>
                    <MetaCell label="Applicant type" value="Individual" />
                    <MetaCell label="Product" value={selected.product} />
                    <MetaCell label="Branch" value={selected.branch} />
                    <MetaCell label="Cart opened" value="Today 08:42" />
                    <MetaCell label="Stuck since" value="Today 09:18 · 3h 24m" tone="red" />
                    <MetaCell label="Last activity" value="Officer sent CIP reminder 12m ago" />
                  </div>

                  <h3 style={{ fontSize: 13, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: 0.4, margin: 'var(--sp-9) 0 var(--sp-5)' }}>
                    Recent activity (top 6)
                  </h3>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, borderLeft: '2px solid var(--ink-200)', paddingLeft: 'var(--sp-7)' }}>
                    {[
                      { t: '09:18', text: 'Stage Applicants → CIP / OFAC (officer BW)' },
                      { t: '09:32', text: 'CIP 3rd-party run returned AMBIGUOUS (retry)' },
                      { t: '09:47', text: 'Officer requested manual review' },
                      { t: '10:12', text: 'Customer re-uploaded DL (page 2)' },
                      { t: '10:34', text: 'QC exception opened (ID mismatch Q1b)' },
                      { t: '11:00', text: 'SLA warning sent · threshold 220m' },
                    ].map((e) => (
                      <li key={e.t} style={{ fontSize: 12, color: 'var(--ink-700)', marginBottom: 'var(--sp-4)' }}>
                        <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--pcb-blue-dark)', marginRight: 8 }}>{e.t}</strong>
                        {e.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {sub === 'timeline' && (
                <p style={{ color: 'var(--ink-600)' }}>Full timeline ships in Wave 3 alongside <code>/v3/sessions/{'{id}'}/events</code>.</p>
              )}
              {sub === 'attempts' && (
                <p style={{ color: 'var(--ink-600)' }}>API attempt log will render here — `/v3/metrics/attempts` planned.</p>
              )}
              {sub === 'notes' && (
                <p style={{ color: 'var(--ink-600)' }}>Session notes render here from <code>/v3/sessions/{'{id}'}/notes</code>.</p>
              )}
            </>
          ) : (
            <p style={{ color: 'var(--ink-600)' }}>Select a session on the left to view its detail.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaCell({ label, value, tone }: { label: string; value: string; tone?: 'red' }): JSX.Element {
  return (
    <div style={{ background: 'var(--ink-50)', borderRadius: 'var(--r-sm)', padding: 'var(--sp-4) var(--sp-5)' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 13, color: tone === 'red' ? 'var(--pcb-red)' : 'var(--ink-900)', fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}
